// Fixed-grid analysis layout — replaces the freeform RGL `AnalysisShell`
// for the post-overhaul ELEKS-aligned UX. Filters live above the layout
// in `<FilterToolbar />`, NOT inside; this layout is purely about the
// 4-column row that contains the data widgets.
//
// Column intent (matches ELEKS screenshot 005 + 008):
//
//   ┌─────┬───────────┬──────────────────────┬────────────┐
//   │     │           │                      │            │
//   │ S   │ List      │ Viewer (FLEX)        │ Filmstrip  │
//   │ k   │ Findings  │                      │            │
//   │ e   │ /Defects  │                      │ thumb900   │
//   │ t   │ tabs      │                      │ ~180 px    │
//   │ c   │           │                      │            │
//   │ h   │ ~280 px   │                      │ ~220 px    │
//   │ ~80 │           │                      │            │
//   │     │           ├──────────────────────┤            │
//   │     │           │ Tiles (under Viewer) │            │
//   │     │           │ ~140 px tall         │            │
//   └─────┴───────────┴──────────────────────┴────────────┘
//
// The viewer is the only flex-grow region — every other column has a
// fixed width so the viewer claims everything else. On screens narrower
// than ~1100 px we fall back to a vertical stack so nothing collapses
// to zero width.
import { Box, Paper, Stack, Typography } from "@mui/material";
import { radius, typography } from "@aeriosiq/ui";

/**
 * D72 names this value explicitly in its migration note: `ANALYSIS_PANEL_RADIUS`
 * was a THIRD card value at 10px, chosen to sit between the 12px utility radius
 * and the 18px card radius. The scale has four steps and this was not one of
 * them.
 *
 * It was introduced deliberately (Christian, 2026-05-09, §"reduce
 * border-radius": the workspace widgets need precise edges so images and chip
 * toolbars line up without a visual halo) and the instinct behind it survives —
 * D72 simply spends it differently. A panel is a panel, so it takes
 * `radius.card`; the tightness the note asked for now comes from the toggles
 * inside, which already measure 12px, exactly `--radius-control`, and therefore
 * needed no work at all.
 */
export const ANALYSIS_PANEL_RADIUS = radius.card;
/**
 * The thumbnail. D72 gives media 12px and has nothing smaller — 4px was below
 * the scale's floor. A thumbnail IS media, so it takes the media step.
 */
export const ANALYSIS_THUMB_RADIUS = radius.media;

interface AnalysisLayoutProps {
  sketch: React.ReactNode;
  list: React.ReactNode;
  viewer: React.ReactNode;
  tiles: React.ReactNode;
  filmstrip: React.ReactNode;
}

const SKETCH_WIDTH = 88;
const LIST_WIDTH = 300;
const FILMSTRIP_WIDTH = 220;
const TILES_HEIGHT = 156;
// Header (AnalysisHeader ~104 px) + h1 row (~56 px) + FilterToolbar
// (~96 px expanded) + page padding (~48 px) ≈ 304 px above this layout.
// Reserve the rest of the viewport for the layout so the viewer fills
// vertically without an outer scrollbar.
// PR-B bumped the column height for the AnalysisHeader's per-user
// ReviewProgressBar (Story 065).
// ANA-R4-LAYOUT-01 — the VerifyAllRow under the FilterToolbar (Story 025) was
// removed (it stole ~56 px and cut off the Viewer/Tiles on a 4K screen), so the
// reserve drops back 360 → 304 to reclaim that canvas height.
const LAYOUT_RESERVE = 304;

function Panel({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <Paper
      variant="outlined"
      role="region"
      aria-label={label}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        borderRadius: `${String(ANALYSIS_PANEL_RADIUS)}px`,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          px: 1.5,
          py: 0.75,
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontSize: typography.size["label-md"],
            fontWeight: typography.weight.semibold,
            letterSpacing: typography.tracking["label-md"],
            color: "text.primary",
            textTransform: "uppercase",
          }}
        >
          {label}
        </Typography>
      </Stack>
      <Box sx={{ flexGrow: 1, minHeight: 0, overflow: "auto" }}>{children}</Box>
    </Paper>
  );
}

export function AnalysisLayout({
  sketch,
  list,
  viewer,
  tiles,
  filmstrip,
}: AnalysisLayoutProps): React.ReactElement {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: 1,
        height: { xs: "auto", lg: `calc(100vh - ${String(LAYOUT_RESERVE)}px)` },
        minHeight: 480,
        // On narrow viewports, fall back to a vertical stack so nothing
        // collapses to zero width.
        flexWrap: { xs: "wrap", lg: "nowrap" },
      }}
    >
      <Box
        sx={{
          flex: { xs: "1 1 100%", lg: `0 0 ${String(SKETCH_WIDTH)}px` },
          height: { xs: 240, lg: "100%" },
          minHeight: 240,
        }}
      >
        <Panel label="Component">{sketch}</Panel>
      </Box>

      <Box
        sx={{
          flex: { xs: "1 1 100%", lg: `0 0 ${String(LIST_WIDTH)}px` },
          height: { xs: 480, lg: "100%" },
          minHeight: 320,
        }}
      >
        <Panel label="Defects · AI Findings">{list}</Panel>
      </Box>

      <Box
        sx={{
          flex: { xs: "1 1 100%", lg: "1 1 0" },
          minWidth: 0,
          height: { xs: "auto", lg: "100%" },
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            minHeight: { xs: 480, lg: 0 },
            display: "flex",
          }}
        >
          <Panel label="Viewer">{viewer}</Panel>
        </Box>
        <Box
          sx={{
            flexShrink: 0,
            height: TILES_HEIGHT,
            display: "flex",
          }}
        >
          <Panel label="Tiles">{tiles}</Panel>
        </Box>
      </Box>

      <Box
        sx={{
          flex: {
            xs: "1 1 100%",
            lg: `0 0 ${String(FILMSTRIP_WIDTH)}px`,
          },
          height: { xs: 360, lg: "100%" },
          minHeight: 320,
        }}
      >
        <Panel label="Filmstrip">{filmstrip}</Panel>
      </Box>
    </Box>
  );
}
